import React, {useMemo} from 'react';
import {
    DoppeDtoAction,
    doppeDtoActionShouldShowActionHeader,
    DoppeDtoActionViewType,
    doppeDtoActionViewTypeIsDisplayedAsButton
} from '../../client-server-common/types/doppe-dto-action';
import {DoppeActionWidgetOpeningMode, useBymoPageProps} from '../../client-server-common';
import {DoppeViewerActionRenderer} from '../doppe-viewer-action-renderer/doppe-viewer-action-renderer';
import {assertDefined, evaluateFunction, evaluateWhenFunction, ObservableValue} from '@wix/devzai-utils-common';
import {BymoActionsMode} from '../../client-server-common/bymo-pages/bymo-actions-settings';
import {DoppeActionView} from '../../doppe-sdk';
import {classes, style} from './doppe-viewer-action.st.css';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {
    useBymoPageViewerComponentsContext
} from '../bymo-page-viewer-components-context/bymo-page-viewer-components-context';
import {
    doppeActionTypeSupportsInPageView,
    WidgetRenderingContext,
    WidgetRenderingContextType
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {DoppeViewerActionContext} from '../doppe-viewer-action-context/doppe-viewer-action-context';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {
    BymoPageActionHtmlElementAttribute
} from "../../client/doppe-app-and-viewer-common/bymo-page-html-elements/bymo-page-html-elements";

export const DoppeViewerAction = React.memo(function DoppeViewerAction(props: DoppeViewerAction.Props) {
    const {
        action,
        renderActionView,
        supportsRenderingFramelessWidgets = false,
        isExpanded = false,
        actionTogglingController,
        fullScreenModeTogglingController,
        actionIndex,
        actionViewRootHtmlAttributes
    } = props;

    const clientServices = useDoppeViewerClientServices();
    const bymoPageProps = useBymoPageProps();
    const pageViewerContext = useBymoPageViewerContext();
    const bymoPageViewerComponentsContextData = useBymoPageViewerComponentsContext();

    const {
        actionsSettings
    } = bymoPageProps;

    const renderingInnerStateController = useMemo(()=>{
        return new ObservableValue(false);
    }, []);

    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(action.actionTypeId),
        `Action type with id '${action.actionTypeId}' isn't registered`);

    const observableCurrentItemIdToRender = useMemo(() => {
        return actionType.resolveListItems ? new ObservableValue<string | null>(null) : undefined;
    }, [ actionType]);

    return (
        <DoppeViewerActionRenderer
            action={action}
            actionIndex={actionIndex}
            actionTogglingController={actionTogglingController}
            fullScreenModeTogglingController={fullScreenModeTogglingController}
        >
            {(actionContext, actionType, observableActionContext) => {

                const canRenderInPage = doppeActionTypeSupportsInPageView(actionType);
                const shouldOpenCTA = actionContext.action.widgetOpeningMode === DoppeActionWidgetOpeningMode.OpenCTA;

                const viewType = evaluateFunction(() => {

                    if (!canRenderInPage && !doppeDtoActionViewTypeIsDisplayedAsButton(actionContext.getActionViewType())) {
                        return DoppeDtoActionViewType.ActionButton;
                    } else if (supportsRenderingFramelessWidgets && actionsSettings.actionsMode === BymoActionsMode.SingleAction && actionType.renderWidget !== undefined) {
                        return DoppeDtoActionViewType.InPageFrameless;
                    } else {
                        return actionContext.getActionViewType();
                    }
                })

                const renderActionWidget = (htmlAttributes: React.HTMLAttributes<any>) => {
                    if (actionType.renderWidget === undefined) {
                        return (
                            <div>{`Action type with id '${action.actionTypeId}' doesn't support inline rendering.`}</div>
                        )
                    } else {
                        // Important: In this commit I changed the target element that the htmlAttributes are applied to.
                        // Until now, the htmlAttributes were passed to renderWidget and assumed that the widget will
                        // apply these attributes on its root. Now I put the htmlAttributes on the surrounding div
                        // that existed here anyway.
                        // If this change causes an issue then we should revert this change but also to consider
                        // removing the surrounding div. Having the surrounding div caused an issue when the card
                        // was expanded - It applied overflow hidden on the surrounding div during the animation,
                        // which caused the inner widget frame (border/shadow) to be cut off.
                        return (
                            <div
                                {...htmlAttributes}
                            >
                                <WidgetRenderingContext.Provider
                                    value={{
                                        widgetRenderingContextType: WidgetRenderingContextType.Card,
                                        renderingInnerStateController: renderingInnerStateController,
                                        observableCurrentItemIdToRender: observableCurrentItemIdToRender,
                                    }}
                                >
                                    {
                                        actionType.renderWidget({
                                            actionContext: actionContext,
                                        }, bymoPageViewerComponentsContextData.actionTypesViewerComponents.get(actionType.id))
                                    }
                                </WidgetRenderingContext.Provider>
                            </div>
                        )
                    }
                }

                const actionButtonProps = actionType.getActionButtonProps?.(actionContext, observableActionContext) ?? {};
                const activateCTA = () => {
                    if (!actionType.activateCTA) {
                        console.log(`Action type with id '${action.actionTypeId}' doesn't support CTA activation.`);
                    } else {
                        actionType.activateCTA(actionContext, observableActionContext);
                    }
                }

                const {
                    className,
                    ...restActionViewRootHtmlAttributes
                } = {
                    ...actionViewRootHtmlAttributes
                }

                const actionViewContextProps = {
                    label: action.name,
                    actionImage: action.icon,
                    bannerMedia: action.bannerMedia,
                    actionDescription: action.callToActionDescription,
                    viewType: viewType,
                    renderActionWidget: renderActionWidget,
                    actionButtonComponentProps: actionButtonProps,
                    activateCTA: activateCTA,
                    isWidgetOpener: (actionType.isWidgetOpener && !shouldOpenCTA) ?? false,
                    skipReportingOnGoalAchievedOnActionButtonClick: evaluateWhenFunction(actionType.skipReportingOnGoalAchievedOnActionButtonClick, actionContext) ?? false,
                    actionContext: actionContext,
                    isExpanded: isExpanded,
                    actionViewRootHtmlAttributes: {
                        className: style(classes.root, {previewMode: pageViewerContext.mode === BymoPageViewerMode.Preview}, className),
                        [BymoPageActionHtmlElementAttribute]: action.id,
                        ...restActionViewRootHtmlAttributes
                    },
                    showActionHeader: doppeDtoActionShouldShowActionHeader(action),
                } satisfies DoppeActionView.ActionContextProps;

                return renderActionView({
                    actionViewContextProps: actionViewContextProps
                })
            }}
        </DoppeViewerActionRenderer>
    )
});

export namespace DoppeViewerAction {

    export interface ActionViewRenderProps {
        actionViewContextProps: DoppeActionView.ActionContextProps;
    }

    export type ActionViewRenderFunction = (renderProps: ActionViewRenderProps) => React.ReactElement;

    export interface Props {
        action: DoppeDtoAction;
        renderActionView: ActionViewRenderFunction;
        supportsRenderingFramelessWidgets?: boolean;
        isExpanded?: boolean;
        actionTogglingController?: DoppeViewerActionContext.ActionTogglingController;
        fullScreenModeTogglingController?: DoppeViewerActionContext.FullScreenModeTogglingController;
        actionIndex?: number;
        actionViewRootHtmlAttributes?: React.HTMLAttributes<any>
    }

}